// RevenueCat IAP helpers.
//
// On iOS native this wires the @revenuecat/purchases-capacitor plugin
// through the lifecycle: configure() at app boot, logIn()/logOut() on auth
// transitions. On web every export is a no-op so the same hooks can be
// called unconditionally from useAuth without platform branching there.
//
// The API key is a public RevenueCat "appl_*" SDK key (not a secret) —
// safe to ship in the client bundle. The webhook secret is the gate that
// keeps premium state changes server-side only.

import { Capacitor } from '@capacitor/core'

let configured = false
// The in-flight configure() — logIn/logOut await it so identity calls can
// never race ahead of configuration. Found 2026-08-05: configure is
// fire-and-forget at boot while logIn fires on SIGNED_IN; if logIn won the
// race, RC stayed ANONYMOUS for the whole session and a purchase filed
// under $RCAnonymousID — which the webhook can't map to a user. On a
// first-ever Android launch there is no persisted RC identity to save you.
let _configurePromise: Promise<void> | null = null

/** Configure the SDK at app boot. Safe to call multiple times — guarded
 *  by an idempotency flag because Capacitor 7 emits the "app start"
 *  callback more than once on cold launch on some devices. */
export function configureRevenueCat(): Promise<void> {
  if (!_configurePromise) {
    _configurePromise = _doConfigure().catch((err) => {
      console.warn('[iap] RevenueCat configure failed', err)
      _configurePromise = null
    }) as Promise<void>
  }
  return _configurePromise
}

async function _doConfigure(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (configured) return
  // Per-platform PUBLIC SDK keys (appl_* / goog_* — publishable, not
  // secrets). Android added 2026-08-05 with Play Billing.
  const apiKey = (Capacitor.getPlatform() === 'android'
    ? import.meta.env.VITE_REVENUECAT_ANDROID_KEY
    : import.meta.env.VITE_REVENUECAT_IOS_KEY) as string | undefined
  if (!apiKey) {
    console.warn(`[iap] RevenueCat key not set for ${Capacitor.getPlatform()} — In-App Purchase disabled.`)
    return
  }
  // No inner try/catch — a failure must reach configureRevenueCat's handler
  // so the memoised promise resets and the next call retries.
  const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor')
  await Purchases.setLogLevel({ level: LOG_LEVEL.WARN })
  await Purchases.configure({ apiKey })
  configured = true
}

/** Identify the user to RevenueCat. Called on SIGNED_IN. The userId we
 *  pass is the GoTrue session.user.id (the auth ID) — the backend webhook
 *  handler falls back to a User.gotrue_id lookup so the user row resolves
 *  correctly either way. */
export async function revenuecatLogIn(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (!userId) return
  try {
    // Never race configure — see _configurePromise note above.
    await configureRevenueCat()
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    await Purchases.logIn({ appUserID: userId })
  } catch (err) {
    console.warn('[iap] RevenueCat logIn failed', err)
  }
}

/** Reset RevenueCat identity on SIGNED_OUT so the next user on a shared
 *  device starts fresh — without this RC would keep the previous user's
 *  appUserID locally and the next purchase would be filed against the
 *  wrong account. */
export async function revenuecatLogOut(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    // Await configure first — same race as logIn. A SIGNED_OUT arriving
    // before boot-time configure resolved would call logOut on an
    // unconfigured SDK (throws, swallowed), leaving the previous user's
    // appUserID cached — the exact wrong-account bug this function prevents
    // (audit 2026-08-06).
    await configureRevenueCat()
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    await Purchases.logOut()
  } catch (err) {
    console.warn('[iap] RevenueCat logOut failed', err)
  }
}
